import { PRIVACY_POLICY, TERMS_AND_CONDITION } from './constants';
import { useState, useLayoutEffect, useEffect } from 'react';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import CryptoJS from 'crypto-js';
import editIcon from '../assets/images/profile/edit_icon.svg';
import deleteIcon from '../assets/images/profile/delete_icon.svg';
import projectsIcon from '../assets/images/profile/projects_icon.svg';
import liveDemoIcon from '../assets/images/profile/live_demo.svg';
import certificationsIcon from '../assets/images/profile/certifications.svg';

const envMode = process.env.REACT_APP_ENV;
export const Toast = ({ msg }) => <>{msg}</>;
export const isUpperCase = (value) => /[A-Z]/.test(value);
export const isLowerCase = (value) => /[a-z]/.test(value);
export const isContainNumber = (value) => /\d/.test(value);
export const isSpecialChar = (value) => /[!@#$%^&*()_+\-=\\[\]{};':"\\|,.<>\\/?]+/.test(value);

export const capitalize = (s) => {
    if (typeof s !== 'string') return '';
    return s
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};
export const capitalizeAll = (s) => {
    if (typeof s !== 'string') return '';
    return s
        .toLowerCase()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const remarksChecker = (value) => {
    const sanitizedValue = value.replace(/[^A-Za-z0-9\s\n!@#$%&*()_\-+=\[\]{}\/?.,;:'"\/]/g, '');
    return sanitizedValue;
};

export const downloadFile = (fileUrl, fileName) => {
    let a = document.createElement('a');
    a.href = fileUrl;
    a.download = fileName;
    a.target = '_blank';
    a.click();
};

export const KEY_WORD = {
    GLOBAL: 'global',
    COMMON_PERMISSION: 'commonPermission',
    INCBOT: 'incbot',
    INCSOURCE: 'incsource',
    INCFEED: 'incfeed',
    INCSERVE: 'incserve',
    INCVID: 'incvid',
    INCEXIT: 'incexit',
    EXPORT_TO_EXCEL: 'exportToExcel',
    ROLES_AND_SUBUSERS: 'rolesAndSubusers',
    CREATE: 'create',
    DELETE: 'delete',
    POSITIONS: 'positions',
    INTERVIEWS: 'interviews',
    COMPANY_PROFILE: 'companyProfile',
    CUSTOM_FIELDS: 'customFields',
    SUPPORT: 'support',
    VIEW: 'view',
    ACCOUNTS_AND_BILLING: 'accountsAndBilling',
    FILE_UPLOAD_SUCCESS_MSG: 'File uploaded successfully.',
    IMAGE_UPLOAD_SUCCESS_MSG: 'Image uploaded successfully.',
    PRODUCT_ID: 'productId',
    LOCALHOST: 'localhost',
    INCRUITER_DOMIAN: 'incruiter.com',
    APPLICANTS: 'applicant',
};

export const nameChecker = (value) => {
    return value?.replace(/[^A-Za-z\s\.]/g, '');
};

export const numberChecker = (value) => {
    return value?.replace(/\D/g, '');
};

export const mailChecker = (value) => {
    return value?.replace(/[^a-zA-Z0-9\_\-\@\.\+]/g, '');
};

// URL validation function for social links
export const validateSocialURL = (url) => {
    if (!url || !url.trim()) return { isValid: true, error: null }; // Empty URLs are allowed

    try {
        const urlObj = new URL(url);
        if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
            return {
                isValid: false,
                error: 'URL must start with http:// or https://',
            };
        }
        return { isValid: true, error: null };
    } catch (error) {
        return {
            isValid: false,
            error: 'Invalid URL format. Please enter a valid URL starting with http:// or https://',
        };
    }
};

// Normalize URL to ensure it has proper protocol
export const normalizeURL = (url) => {
    if (!url || !url.trim()) return url;

    if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }

    return `https://${url}`;
};

export const getAccessToken = Cookies.get(`token_${envMode}`) ?? '';

const DEFAULT_ERROR_MSG = 'Something went wrong. Please try again.';
const generateError = (msg) => ({ status: false, msg });

export const validateFile = async ({
    file,
    maxSizeMB = 5,
    allowedTypes = [],
    endpoint = '',
    emptyMsg = 'File is empty or undefined',
    sizeMsg = 'The file size exceeds the limit',
    typeMsg = 'Invalid file type',
    fallbackMsg = 'Invalid file. Please upload a valid file',
}) => {
    try {
        if (!file) return generateError(emptyMsg);
        if (file.size > maxSizeMB * 1024 * 1024) return generateError(sizeMsg);
        const allowedTypesArray = Array.isArray(allowedTypes)
            ? allowedTypes
            : allowedTypes.split(',').map((type) => type.trim());

        if (!allowedTypesArray.includes(file.type)) return generateError(typeMsg);

        const formData = new FormData();
        formData.append('file', file);

        const url = `${generateUrl('interview_backend')}/${endpoint}`;
        const response = await fetch(url, { method: 'POST', body: formData });
        const res = await response.json();

        return {
            status: res?.status ?? false,
            msg: res?.message || fallbackMsg,
        };
    } catch {
        return generateError(DEFAULT_ERROR_MSG);
    }
};

export const FILE_TYPES = {
    IMAGE: 'image/jpeg, image/png, image/jpg',
    ZIP: 'zip, application/octet-stream, application/zip, application/x-zip, application/x-zip-compressed',
    XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    PDF_WORD: [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
        'application/msword', // .doc
        'application/x-msword',
        'application/x-cfb',
        'application/vnd.ms-word.document.macroenabled.12',
    ].join(', '),
};

export const checkFileBlank = (file) =>
    validateFile({
        file,
        maxSizeMB: 5,
        allowedTypes: FILE_TYPES.PDF_WORD,
        endpoint: 'validateFile',
        emptyMsg: 'File is empty or undefined',
        sizeMsg: 'The file size exceeds the 5MB limit',
        typeMsg: 'Invalid file type. Only .pdf, .docx, .doc are allowed',
        fallbackMsg: 'Invalid file type. Please upload a valid file',
    });

export const checkImageBlank = (file) =>
    validateFile({
        file,
        maxSizeMB: 5,
        allowedTypes: FILE_TYPES.IMAGE,
        endpoint: 'validateImage',
        emptyMsg: 'Image is empty or undefined',
        sizeMsg: 'The image size exceeds the 5MB limit',
        typeMsg: 'Invalid image type. Only .jpg, .jpeg, and .png are allowed.',
        fallbackMsg: 'Invalid image type. Please upload a valid image',
    });

export const checkXLSXBlank = (file) =>
    validateFile({
        file,
        maxSizeMB: 20,
        allowedTypes: FILE_TYPES.XLSX,
        endpoint: 'validateXlsx',
        emptyMsg: 'XLSX file is empty or undefined',
        sizeMsg: 'The XLSX file size exceeds the 20MB limit',
        typeMsg: 'Invalid file type. Only .xlsx is allowed',
        fallbackMsg: 'Invalid XLSX file. Please upload a valid .xlsx file',
    });

export const checkZipBlank = (file) =>
    validateFile({
        file,
        maxSizeMB: 100,
        allowedTypes: FILE_TYPES.ZIP,
        endpoint: 'validateZip',
        emptyMsg: 'ZIP file is empty or undefined',
        sizeMsg: 'The ZIP file size exceeds the 100MB limit',
        typeMsg: 'Invalid file type. Only .zip is allowed',
        fallbackMsg: 'Invalid ZIP file. Please upload a valid .zip file',
    });

export const checkMixBlank = (file) =>
    validateFile({
        file,
        maxSizeMB: 5,
        allowedTypes: [FILE_TYPES.PDF_WORD, FILE_TYPES.IMAGE].join(', '),
        endpoint: 'validateAllFile',
        emptyMsg: 'File is empty or undefined',
        sizeMsg: 'The file size exceeds the 5MB limit',
        typeMsg: 'Invalid file type. Only .jpg, .jpeg, .png, .pdf, .docx, .doc are allowed',
        fallbackMsg: 'Invalid file type. Please upload a valid file',
    });

export const getHigherExperience = (experience) => {
    if (!experience) return null; // Handle null, undefined, or empty values

    if (typeof experience !== 'string') {
        console.warn('Expected a string for experience, but received:', experience);
        return null; // Return null or handle as appropriate
    }

    if (experience.includes('-')) {
        const values = experience.split('-').map(Number); // Split and convert to numbers
        return Math.max(...values); // Return the highest value
    } else if (experience.endsWith('+')) {
        return Number(experience.replace('+', '')); // Remove '+' and convert to a number
    }

    return Number(experience); // Fallback for single numeric values
};

export const useWindowSize = () => {
    const [size, setSize] = useState([0, 0]);
    useLayoutEffect(() => {
        function updateSize() {
            setSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);
    return size;
};

export const clearOldCookies = () => {
    let cookieNames = [];
    const cookiesDevName = [
        'token_dev',
        'incserveToken_dev',
        'iCuD_dev',
        'iCcD_dev',
        'iCpD_dev',
        'ic_p_dev',
        'incD_dev',
    ];
    const cookiesTestName = [
        'token_test',
        'incserveToken_test',
        'iCuD_test',
        'iCcD_test',
        'iCpD_test',
        'ic_p_test',
        'incD_test',
    ];
    const cookiesProdName = [
        'token_prod',
        'incserveToken_prod',
        'iCuD_prod',
        'iCcD_prod',
        'iCpD_prod',
        'ic_p_prod',
        'incD_prod',
    ];
    if (envMode === 'dev') {
        cookieNames = [...cookiesTestName, ...cookiesProdName];
    } else if (envMode === 'test') {
        cookieNames = [...cookiesDevName, ...cookiesProdName];
    } else {
        cookieNames = [...cookiesTestName, ...cookiesDevName];
    }
    cookieNames.forEach((name) =>
        Cookies.remove(`${name}_${envMode}`, {
            path: '/',
            domain: window.location.hostname,
        })
    );
};

export const clearCookies = () => {
    const cookieNames = ['token', 'incserveToken', 'iCuD', 'iCcD', 'iCpD', 'ic_p'];
    cookieNames.forEach((name) => {
        Cookies.remove(`${name}_${envMode}`, {
            path: '/',
            domain: window.location.hostname,
        });
    });
};

export const useOutsideAlerterCallBack = (ref, callback) => {
    useEffect(() => {
        function handleClickOutside(event) {
            if (ref.current && !ref.current.contains(event.target)) {
                callback();
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref, callback]);
};

export const useOutsideAlerter = (ref, setIsOpen) => {
    useEffect(() => {
        function handleClickOutside(event) {
            if (ref.current && !ref.current.contains(event.target)) {
                setIsOpen(false);
            }
        }

        // Bind the event listener
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [ref, setIsOpen]);
};

export const notificationRedirect = (data, navigate) => {
    const getPath = (itemType) => {
        const itemTypeLower = itemType?.toUpperCase()?.trim();
        switch (itemTypeLower) {
            case 'INTERVIEWS':
                return '/interviews';
            case 'SUPPORT':
                return '/support';
            default:
                return '';
        }
    };
    const getOperator = (urlPath) => {
        return urlPath.includes('?') ? '&' : '?';
    };
    const search = data?.data?.search;
    const itemType = data?.data?.itemType;
    const urlPath = getPath(itemType);
    if (urlPath) {
        navigate(urlPath + `${getOperator(urlPath)}search=${encodeURIComponent(search)}`);
    }
};

export const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(remainingSeconds).padStart(2, '0');
    return `${formattedMinutes}:${formattedSeconds}`;
};

export const generateQuestionSuffix = (question, time) => {
    const listA = [
        `${question}\nYou have ${time} seconds to begin your answer. You can record your response after the beep sound.`,
        `${question}\nYou have ${time} seconds to begin your answer. You can start after the beep sound.`,
        `${question}\nPlease answer after the beep sound.`,
        `${question}\nYou will have ${time} seconds to prepare before. Please answer after the beep sound.`,
    ];
    const randomIndexA = Math.floor(Math.random() * listA.length);
    return listA[randomIndexA];
};

export const removeQuotes = (input) => {
    if (typeof input === 'string') {
        // Remove leading and trailing quotes if they exist
        return input.replace(/^"(.*)"$/, '$1');
    }
    return input; // Return as-is if it's not a string
};

export const getLatencyText = (name) => {
    const lists = [`Okay ${name}.`, `Alright ${name}.`];
    // Select a random index from the lists array
    const randomIndex = Math.floor(Math.random() * lists.length);
    return lists[randomIndex];
};

export const noAnswer = (name) => {
    const value = `Okay, no worries ${name}.`;
    return value;
};

export const validateEmail = (email) => {
    const regex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+\.[A-Za-z]{2,}$/;
    return regex.test(email);
};

export const toastMessage = (type, message) => {
    toast[type](message, { autoClose: '5000', toastId: message });
};

// Check if the device is mobile or tablet
export const isMobileOrTabletDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return (
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|tablet/.test(userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    ); // iPadOS detection
};

// Check if the device is strictly mobile (not tablet or iPad)
export const isMobileDevice = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return (
        /android|webos|iphone|ipod|blackberry|iemobile|opera mini/.test(userAgent) &&
        !(navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    ); // Exclude iPadOS
};

export const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    const browserName = (() => {
        if (userAgent.indexOf('Chrome') !== -1) return 'Chrome';
        if (userAgent.indexOf('Firefox') !== -1) return 'Firefox';
        if (userAgent.indexOf('Safari') !== -1) return 'Safari';
        if (userAgent.indexOf('Edge') !== -1) return 'Edge';
        if (userAgent.indexOf('Opera') !== -1 || userAgent.indexOf('OPR') !== -1) return 'Opera';
        if (userAgent.indexOf('MSIE') !== -1 || userAgent.indexOf('Trident/') !== -1) return 'Internet Explorer';
        return 'Unknown';
    })();

    return {
        browserName,
        browserVersion: navigator.appVersion,
    };
};

export const getBrowserInfoMessage = () => {
    let message;
    let browserInfo = getBrowserInfo();
    message = `Browser: ${browserInfo?.browserName}, Browser Version: ${browserInfo?.browserName}, Device Type: ${
        isMobileDevice() ? 'Mobile' : isMobileOrTabletDevice() ? 'Tablet' : 'Desktop/Laptop'
    }`;
    return message;
};
export const prepareTimeOverLink =
    'https://storage.googleapis.com/incruiter-media/audio_file/audio-1701862643-82158.mp3';
export const answerTimeOverLink =
    'https://storage.googleapis.com/incruiter-media/audio_file/audio-1701862720-92073.mp3';
export const interviewTimeOverLink =
    'https://storage.googleapis.com/incruiter-media/audio_file/audio-1701862799-26131.mp3';

export const generateUrl = (urlMode) => {
    const envMode = process.env.REACT_APP_ENV;
    let frontendUrlPreFix, backendUrlPreFix, externalUrl, frontendPUrlPrefix, interviewUrl, socketUrlPrefix;
    let finalUrl;
    if (envMode === 'prod') {
        interviewUrl = 'candidate.';
        frontendPUrlPrefix = '';
        frontendUrlPreFix = '';
        socketUrlPrefix = '';
        backendUrlPreFix = 'api.';
        externalUrl = 'https://prod-in2.100ms.live/hmsapi/incruiter-prod.app.100ms.live/';
    } else if (envMode === 'test' || envMode === 'preprod') {
        interviewUrl = 'test.v1.candidate.';
        frontendPUrlPrefix = 'test.';
        frontendUrlPreFix = 'test.v1.';
        socketUrlPrefix = 'test-';
        backendUrlPreFix = 'test.api.';
        externalUrl = 'https://prod-in2.100ms.live/hmsapi/incruiter-preprod-bot-room.app.100ms.live/';
    } else if (envMode === 'dev2') {
        interviewUrl = 'dev.v2.candidate.';
        frontendPUrlPrefix = 'dev.';
        frontendUrlPreFix = 'dev.v2.';
        socketUrlPrefix = 'dev-';
        backendUrlPreFix = 'dev.v2.api.';
        externalUrl = 'https://prod-in2.100ms.live/hmsapi/incruiter-dev-meet.app.100ms.live/';
    } else {
        interviewUrl = 'dev.v1.candidate.';
        frontendPUrlPrefix = 'dev.';
        frontendUrlPreFix = 'dev.v1.';
        socketUrlPrefix = 'dev-';
        backendUrlPreFix = 'dev.api.';
        externalUrl = 'https://prod-in2.100ms.live/hmsapi/incruiter-dev-meet.app.100ms.live/';
    }

    if (urlMode === 'token_generation') {
        finalUrl = externalUrl;
    } else if (urlMode === 'website') {
        finalUrl = `https://${frontendUrlPreFix}incruiter.com`;
    } else if (urlMode === 'socket') {
        finalUrl = `https://${socketUrlPrefix}video-feedback-system.incruiter.com`;
    } else if (urlMode === 'proctoring') {
        finalUrl = `https://${socketUrlPrefix}proctoring.incruiter.com`;
    } else if (urlMode === 'interview_backend') {
        finalUrl = `https://${backendUrlPreFix}incruiter.com/interview-system/api/v1`;
    } else if (urlMode === 'video_backend') {
        finalUrl = `https://${backendUrlPreFix}incruiter.com/video-feedback-system/api/v1/video-platform`;
    } else if (urlMode === 'meeting') {
        finalUrl = `https://${interviewUrl}incruiter.com`;
    } else if (urlMode === 'user_backend') {
        finalUrl = `https://${backendUrlPreFix}incruiter.com/user-auth-system/api/v1`;
    } else if (urlMode === PRIVACY_POLICY) {
        finalUrl = `https://${frontendUrlPreFix}incruiter.com/privacy-policy`;
    } else if (urlMode === TERMS_AND_CONDITION) {
        finalUrl = `https://${frontendUrlPreFix}incruiter.com/terms-and-condition`;
    } else if (urlMode === 'conversational-ai') {
        finalUrl = `wss://${frontendPUrlPrefix}rms.incruiter.com/`;
    } else if (urlMode === 'mock-interview') {
        finalUrl = `https://${backendUrlPreFix}incruiter.com/candidate-listing-system/api/v1/mock-interview`;
    } else if (urlMode === 'mock-interview-dev2') {
        finalUrl = `https://${backendUrlPreFix}incruiter.com/candidate-listing-system/api/v1`;
    }
    return finalUrl;
};

export const isValidDataURI = (dataURI) => {
    return dataURI && dataURI.length > 10 && !dataURI.includes('png');
};

export const parseMarkdown = (markdownContent) => {
    // Replace Markdown syntax with HTML equivalents
    const htmlContent = markdownContent
        // Headings
        .replace(/^# (.+)$/gm, '<h1>$1</h1>')
        .replace(/^## (.+)$/gm, '<h2>$1</h2>')
        .replace(/^### (.+)$/gm, '<h3>$1</h3>')
        .replace(/^#### (.+)$/gm, '<h4>$1</h4>')
        .replace(/^##### (.+)$/gm, '<h5>$1</h5>')
        .replace(/^###### (.+)$/gm, '<h6>$1</h6>')

        // Bold and Italic
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')

        // Lists
        .replace(/^\s*-\s(.+)/gm, '<li>$1</li>')
        .replace(/^\s*1\.\s(.+)/gm, '<li>$1</li>')
        .replace(/^\s*\d+\.\s(.+)/gm, '<li>$1</li>')
        .replace(/<\/li><li>/g, '</li><li><ul>') // Wrap list items in <ul> tags
        .replace(/<\/li><\/ul><li>/g, '</li><li>') // Remove extra <ul> tags

        // Links
        .replace(/\[(.+?)\]\((\S+?)\)/g, '<a href="$2">$1</a>')

        // Images
        .replace(/!\[(.*?)\]\((.*?)\)/g, '<img alt="$1" src="$2" />')

        // Blockquotes
        .replace(/^\>(.*)$/gm, '<blockquote>$1</blockquote>')

        // Code blocks
        .replace(/```(.+?)\n([\s\S]+?)\n```/g, '<pre><code class="$1">$2</code></pre>')
        .replace(/``(.+?)``/g, '<code>$1</code>');

    return htmlContent;
};

export const getAssetSrc = (assetName) =>
    `https://incruiter.blob.core.windows.net/frontend/frontend-interview/assets/${assetName}`;

export const practiceQuestion = (number) => {
    let questionSet = null;

    switch (parseInt(number)) {
        case 1:
            questionSet = {
                response: `Hello, I hope you’re doing well.
I'm glad to have you here today.
I appreciate you taking the time to interview with us.
I'm looking forward to learning more about you and your experiences.
So, let's get started.
Tell me about yourself?.
You have 10 seconds to begin your answer. You can record your response after the beep sound.`,
                audioUrl: 'https://storage.googleapis.com/incruiter-media/audio_file_azure/audio-1722939778-117535.mp3',
                timeToPrepare: 10,
                timeToAnswer: 60,
                questionType: 'general',
                domain: 'Technical',
                question: 'Tell me about yourself?',
                questionAnswer: '',
            };
            break;
        case 2:
            questionSet = {
                response: `Thanks for your response.
Let's move on to the next topic of discussion.
Why are you interested in this position?
You have 10 seconds to begin your answer.
You can record your response after the beep sound.`,
                audioUrl: 'https://storage.googleapis.com/incruiter-media/audio_file_azure/audio-1723010317-117194.mp3',
                timeToPrepare: 10,
                timeToAnswer: 60,
                questionType: 'general',
                domain: 'Technical',
                question: 'Why are you interested in this position?',
                questionAnswer: '',
            };
            break;
        case 3:
            questionSet = {
                response: `Thanks for your response.
Let's move on to the next topic of discussion.
Describe your current job responsibilities.
You have 10 seconds to begin your answer.
You can record your response after the beep sound.`,
                audioUrl: 'https://storage.googleapis.com/incruiter-media/audio_file_azure/audio-1723010355-39097.mp3',
                timeToPrepare: 10,
                timeToAnswer: 60,
                questionType: 'general',
                domain: 'Technical',
                question: 'Describe your current job responsibilities.',
                questionAnswer: '',
            };
            break;
        case 4:
            questionSet = {
                response: `That's all from my side.
Thank you for your time and for sharing your experiences and insights with us.
Good luck! Your practice interview has been completed.
Please click on the SUBMIT button to end the practice interview.`,
                audioUrl: 'https://storage.googleapis.com/incruiter-media/audio_file_azure/audio-1723010418-98437.mp3',
                timeToPrepare: 10,
                timeToAnswer: 60,
                questionType: 'general',
                domain: 'Technical',
                question: `That's all from my side.
Thank you for your time and for sharing your experiences and insights with us.
Good luck! Your practice interview has been completed.
Please click on the SUBMIT button to end the practice interview.`,
                questionAnswer: '',
            };
            break;
        default:
            questionSet = {
                response: `Hello, I hope you’re doing well.
I'm glad to have you here today.
I appreciate you taking the time to interview with us.
I'm looking forward to learning more about you and your experiences.
So, let's get started.
Tell me about yourself?.
You have 10 seconds to begin your answer. You can record your response after the beep sound.`,
                audioUrl: 'https://storage.googleapis.com/incruiter-media/audio_file_azure/audio-1722939778-117535.mp3',
                timeToPrepare: 10,
                timeToAnswer: 60,
                questionType: 'general',
                domain: 'Technical',
                question: 'Tell me about yourself?',
                questionAnswer: '',
            };
            break;
    }

    return questionSet;
};

const words = [
    'Zero',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
];

const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

const getTwoDigitWord = (num) => {
    if (num < 20) return words[num];
    const ten = Math.floor(num / 10);
    const unit = num % 10;
    return tens[ten] + (unit ? ' ' + words[unit] : '');
};

export const Rs = (amount) => {
    if (!amount) return 'Zero';

    const units = ['', 'Thousand', 'Lakh', 'Crore'];
    let num = parseInt(amount.toString().replace(/,/g, ''), 10);
    if (num === 0) return 'Zero';

    let result = '';
    const parts = [];

    parts.push(num % 1000); // last 3 digits
    num = Math.floor(num / 1000);

    for (let i = 0; i < 3 && num > 0; i++) {
        parts.push(num % 100);
        num = Math.floor(num / 100);
    }

    for (let i = parts.length - 1; i >= 0; i--) {
        if (parts[i] !== 0) {
            result += getTwoDigitWord(parts[i]) + ' ' + (units[i] ? units[i] + ' ' : '');
        }
    }

    const hundredPart = parts[0];
    if (hundredPart >= 100) {
        const h = Math.floor(hundredPart / 100);
        const rem = hundredPart % 100;
        result = result.replace(
            getTwoDigitWord(hundredPart),
            words[h] + ' Hundred' + (rem ? ' ' + getTwoDigitWord(rem) : '')
        );
    }

    return result.trim();
};

export const RsPaise = (n) => {
    if (!n) return 'Zero Only';

    const nums = n.toString().split('.');
    const whole = Rs(nums[0]);
    let fraction = '';

    if (nums[1]) {
        const decimalPart = nums[1].slice(0, 2).padEnd(2, '0');
        const fractionWords = Rs(parseInt(decimalPart, 10));
        if (fractionWords && fractionWords !== 'Zero') {
            fraction = ' and ' + fractionWords + ' Paise';
        }
    }

    return `Rupees ${whole}${fraction} Only`.replace(/\s+/g, ' ').trim();
};

export const redirectPermission = () => {
    localStorage.clear();
    clearCookies();
    window.location.replace(generateUrl('website') + '/candidate/login');
};

export const decryptData = (encryptedText, ENCRYPTION_KEY_HEX) => {
    try {
        if (!encryptedText || typeof encryptedText !== 'string' || !encryptedText.includes(':')) {
            throw new Error('Invalid encrypted text format');
        }

        // Extract IV and encrypted data from Base64
        const [ivBase64, encryptedBase64] = encryptedText.split(':');

        if (!ivBase64 || !encryptedBase64) {
            throw new Error('Invalid encryption format: Missing IV or encrypted data');
        }

        // Convert IV from Base64 to WordArray
        const iv = CryptoJS.enc.Base64.parse(ivBase64);
        const key = CryptoJS.enc.Utf8.parse(ENCRYPTION_KEY_HEX);
        // Decrypt using Base64 encoding
        const decrypted = CryptoJS.AES.decrypt({ ciphertext: CryptoJS.enc.Base64.parse(encryptedBase64) }, key, {
            iv,
            mode: CryptoJS.mode.CBC,
            padding: CryptoJS.pad.Pkcs7,
        });

        const value = decrypted.toString(CryptoJS.enc.Utf8);

        if (!value) {
            throw new Error('Decryption failed: Empty result');
        }

        return value;
    } catch (error) {
        console.error('❌ Decryption Error:', error.message);
        return null;
    }
};
export const handleFileUpload = async (setProfileData, setLoading, setError, transformApiData) => {
    setLoading(true);
    try {
        const token = getAccessToken;

        // Add cache-busting parameter to ensure fresh data
        const response = await fetch(`${generateUrl('mock-interview-dev2')}/resume-parser/get-resume-data`, {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${token}`,
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache',
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
        }

        const responseData = await response.json();

        // Transform the API data to match our component structure
        const transformedData = transformApiData(responseData?.result);
        if (!transformedData) {
            throw new Error('Failed to transform API data');
        }

        setProfileData(transformedData);
    } catch (error) {
        console.log('error', error);
        // Instead of using mock data, show an error state
        setProfileData(null);
        setError('Something went wrong. Please contact support@incruiter.com');
        toastMessage('error', 'Something went wrong. Please contact support@incruiter.com');
    } finally {
        setLoading(false);
    }
};

export const updateResumeAndProfilePic = async ({ resume, fullName, image, onSuccess }) => {
    try {
        const token = getAccessToken;

        const formData = new FormData();

        // Add only if provided
        if (resume) formData.append('resume', resume); // File or Blob
        if (fullName) formData.append('fullName', fullName); // String
        if (image) formData.append('photo', image); // File or Blob

        const response = await fetch(
            `${generateUrl('mock-interview-dev2')}/mock-interview/auth/user-profile/update-v2`,
            {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
        }

        const responseData = await response.json();

        // Check if the API call was successful and call onSuccess callback if provided
        if (responseData?.success === true && typeof onSuccess === 'function') {
            onSuccess(responseData);
        }

        return responseData;
    } catch (error) {
        console.log('error', error);
        throw error; // Re-throw the error so calling code can handle it
    }
};

export const overWriteResumeAndProfilePic = async ({ resume, onSuccess, onFailure }) => {
    try {
        const token = getAccessToken;

        const formData = new FormData();

        // Add only if provided
        if (resume) formData.append('resume', resume); // File or Blob

        const response = await fetch(`${generateUrl('mock-interview-dev2')}/resume-parser/fetch-resume-uploaded-pdf`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: formData,
        });

        const responseData = await response.json();

        // Check if the API call was successful and call onSuccess callback if provided
        if (responseData?.result?.code === 200 && typeof onSuccess === 'function') {
            onSuccess(responseData?.result?.data);
        } else {
            if (typeof onFailure === 'function') {
                onFailure(responseData?.message);
            }
        }

        return responseData;
    } catch (error) {
        console.log('error', error);
        throw error; // Re-throw the error so calling code can handle it
    }
};

export const handleAddSkill = async (skillPayload, onSuccess, onFailure) => {
    try {
        const token = getAccessToken;

        const response = await fetch(`${generateUrl('mock-interview-dev2')}`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
            },
            body: skillPayload,
        });

        const responseData = await response.json();

        // Check if the API call was successful and call onSuccess callback if provided
        if (responseData?.result?.code === 200 && typeof onSuccess === 'function') {
            onSuccess(responseData);
        } else {
            if (typeof onFailure === 'function') {
                onFailure(responseData?.message);
            }
        }

        return responseData;
    } catch (error) {
        console.log('error', error);
        throw error; // Re-throw the error so calling code can handle it
    }
};

// Add new skill API function
export const addSkill = async (skillData, onSuccess, onFailure) => {
    try {
        const token = getAccessToken;

        const response = await fetch(`${generateUrl('mock-interview-dev2')}/resume-parser/add-single-skill`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(skillData),
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
        }

        const responseData = await response.json();

        // Check if the API call was successful and call onSuccess callback if provided
        if (responseData?.success === true && typeof onSuccess === 'function') {
            onSuccess(responseData);
        } else {
            if (typeof onFailure === 'function') {
                onFailure(responseData?.message || 'Failed to add skill');
            }
        }

        return responseData;
    } catch (error) {
        console.log('error', error);
        if (typeof onFailure === 'function') {
            onFailure(error.message || 'Failed to add skill');
        }
        throw error; // Re-throw the error so calling code can handle it
    }
};

// Edit skill API function
export const editSkill = async (skillId, skillData, onSuccess, onFailure) => {
    try {
        const token = getAccessToken;

        const response = await fetch(
            `${generateUrl('mock-interview-dev2')}/resume-parser/update-single-skill/${skillId}`,
            {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(skillData),
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
        }

        const responseData = await response.json();

        // Check if the API call was successful and call onSuccess callback if provided
        if (responseData?.success === true && typeof onSuccess === 'function') {
            onSuccess(responseData);
        } else {
            if (typeof onFailure === 'function') {
                onFailure(responseData?.message || 'Failed to update skill');
            }
        }

        return responseData;
    } catch (error) {
        console.log('error', error);
        if (typeof onFailure === 'function') {
            onFailure(error.message || 'Failed to update skill');
        }
        throw error; // Re-throw the error so calling code can handle it
    }
};

// Delete skill API function
export const deleteSkill = async (skillId, onSuccess, onFailure) => {
    try {
        const token = getAccessToken;

        const response = await fetch(
            `${generateUrl('mock-interview-dev2')}/resume-parser/delete-single-skill/${skillId}`,
            {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            }
        );

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
        }

        const responseData = await response.json();

        // Check if the API call was successful and call onSuccess callback if provided
        if (responseData?.success === true && typeof onSuccess === 'function') {
            onSuccess(responseData);
        } else {
            if (typeof onFailure === 'function') {
                onFailure(responseData?.message || 'Failed to delete skill');
            }
        }

        return responseData;
    } catch (error) {
        console.log('error', error);
        if (typeof onFailure === 'function') {
            onFailure(error.message || 'Failed to delete skill');
        }
        throw error; // Re-throw the error so calling code can handle it
    }
};

export const renderEducationCard = (education, handleEditEducation, handleDeleteEducation) => {
    if (!education || typeof education !== 'object') {
        return null;
    }

    // Check if handlers are provided (for public view, they will be null)
    const showActions = handleEditEducation && handleDeleteEducation;

    return (
        <div key={education.id} className="education-card">
            <div className="education-content">
                <div className="education-header">
                    <div className="education-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
                            <path d="M6 12v5c3 3 9 3 12 0v-5" />
                        </svg>
                    </div>
                    <div className="education-title">
                        <h3>{education.designation || education.degree || 'N/A'}</h3>
                        <div className="institution-info">
                            <span className="institution-name">{education.institution || 'N/A'}</span>
                            <span className="separator">•</span>
                            <span className="location">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                    class="lucide lucide-map-pin-icon lucide-map-pin"
                                >
                                    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
                                    <circle cx="12" cy="10" r="3" />
                                </svg>
                                {education.location || 'N/A'}
                            </span>
                        </div>
                        <div className="education-meta">
                            <div className="meta-item">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                                <span>
                                    {/* {education.startDate || 'N/A'} - {education.endDate || 'N/A'} */}
                                    {typeof (education.start_date || education.startDate) === 'object'
                                        ? formatDisplayDate(education.start_date || education.startDate)
                                        : education.start_date || education.startDate || 'N/A'}{' '}
                                    -{' '}
                                    {typeof (education.end_date || education.endDate) === 'object'
                                        ? formatDisplayDate(education.end_date || education.endDate)
                                        : education.end_date || education.endDate || 'N/A'}
                                </span>
                            </div>
                            <div className="meta-item">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12,6 12,12 16,14" />
                                </svg>
                                <span>{education.duration || 'N/A'}</span>
                            </div>
                            <div className="meta-item">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                    <circle cx="12" cy="7" r="4" />
                                </svg>
                                <span>Grade: {education.grade || 'N/A'}</span>
                            </div>
                        </div>
                        <div className="education-description">
                            <p>{education.description || 'No description available.'}</p>
                        </div>
                    </div>
                    {showActions && (
                        <div className="education-actions">
                            <button className="education-edit-btn" onClick={() => handleEditEducation(education)}>
                                <img src={editIcon} alt="Edit" />
                            </button>
                            <button className="education-delete-btn" onClick={() => handleDeleteEducation(education)}>
                                <img src={deleteIcon} alt="Delete" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const renderProjectCard = (project, handleEditProject, handleDeleteProject) => {
    if (!project || typeof project !== 'object') {
        console.error('Invalid project item:', project);
        return null;
    }

    // Check if handlers are provided (for public view, they will be null)
    const showActions = handleEditProject && handleDeleteProject;

    return (
        <div key={project._id} className="project-card">
            <div className="project-content">
                <div className="project-header">
                    <div className="project-icon">
                        <img src={projectsIcon} alt="Projects" />
                    </div>
                    <div className="project-title">
                        <h3>{project.name || project.title || 'N/A'}</h3>
                        <div className="project-type-status">
                            <span className="project-type">
                                {project.project_type || project.projectType || project.type || 'N/A'}
                            </span>
                            <span
                                className="status-dot"
                                style={{
                                    backgroundColor: project.is_working === true ? '#f97316' : '#10b981',
                                }}
                            ></span>
                            <span
                                className="project-status"
                                style={{ color: project.is_working === true ? '#f97316' : '#10b981' }}
                            >
                                {project.is_working === true ? 'In Progress' : 'Completed' || 'N/A'}
                            </span>
                        </div>
                        <div className="project-details">
                            <div className="project-meta">
                                <div className="meta-item">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                        <line x1="16" y1="2" x2="16" y2="6" />
                                        <line x1="8" y1="2" x2="8" y2="6" />
                                        <line x1="3" y1="10" x2="21" y2="10" />
                                    </svg>
                                    <span>
                                        {typeof (project.start_date || project.startDate) === 'object'
                                            ? formatDisplayDate(project.start_date || project.startDate)
                                            : project.start_date || project.startDate || 'N/A'}{' '}
                                        -{' '}
                                        {typeof (project.end_date || project.endDate) === 'object'
                                            ? formatDisplayDate(project.end_date || project.endDate)
                                            : project.end_date || project.endDate || 'N/A'}
                                    </span>
                                </div>
                                <div className="meta-item">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10" />
                                        <polyline points="12,6 12,12 16,14" />
                                    </svg>
                                    <span>{project.duration || 'N/A'}</span>
                                </div>
                            </div>
                            <div className="project-description">
                                <p>{project.summary || project.description || 'No description available.'}</p>
                            </div>
                            {project.technologies && project.technologies.length > 0 && (
                                <div className="project-technologies">
                                    <div className="technologies-label">Technologies:</div>
                                    <div className="technologies-list">
                                        {project.technologies.map((tech, techIndex) => (
                                            <span key={techIndex} className="technology-tag">
                                                {tech}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {(project.project_url || project.projectUrl) && (
                                <div className="project-demo">
                                    <a
                                        href={project.project_url || project.projectUrl}
                                        className="live-demo-link"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <img src={liveDemoIcon} alt="Live Demo" />
                                        Live Demo
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                    {showActions && (
                        <div className="project-actions">
                            <button className="project-edit-btn" onClick={() => handleEditProject(project)}>
                                <img src={editIcon} alt="Edit" />
                            </button>
                            <button className="project-delete-btn" onClick={() => handleDeleteProject(project)}>
                                <img src={deleteIcon} alt="Delete" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const renderCertificationCard = (certification, handleEditCertification, handleDeleteCertification) => {
    if (!certification || typeof certification !== 'object') {
        return null;
    }

    // Check if handlers are provided (for public view, they will be null)
    const showActions = handleEditCertification && handleDeleteCertification;

    return (
        <div key={certification._id} className="certification-card">
            <div className="certification-content">
                <div className="certification-header">
                    <div className="certification-icon">
                        <img src={certificationsIcon} alt="Certifications" />
                    </div>
                    <div className="certification-title">
                        <h3>{certification.certification_name || certification.title || 'N/A'}</h3>
                        <div className="certification-issuer-status">
                            <span className="certification-issuer">
                                {certification.issued_by || certification.issuer || 'N/A'}
                            </span>
                            <span
                                className="status-dot"
                                style={{
                                    backgroundColor:
                                        certification.statusColor === 'green'
                                            ? '#10b981'
                                            : certification.statusColor === 'red'
                                              ? '#ef4444'
                                              : '#f97316',
                                }}
                            ></span>
                            <span
                                className="certification-status"
                                style={{
                                    color:
                                        certification.statusColor === 'green'
                                            ? '#10b981'
                                            : certification.statusColor === 'red'
                                              ? '#ef4444'
                                              : '#f97316',
                                }}
                            >
                                {certification.status || 'N/A'}
                            </span>
                        </div>
                        <div className="certification-details">
                            <div className="certification-meta">
                                <div className="meta-item">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                        <line x1="16" y1="2" x2="16" y2="6" />
                                        <line x1="8" y1="2" x2="8" y2="6" />
                                        <line x1="3" y1="10" x2="21" y2="10" />
                                    </svg>
                                    <span>Issued: {certification.issuedDate || 'N/A'}</span>
                                </div>
                                <div className="meta-item">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <circle cx="12" cy="12" r="10" />
                                        <polyline points="12,6 12,12 16,14" />
                                    </svg>
                                    <span>Expires: {certification.expiresDate || 'N/A'}</span>
                                </div>
                                <div className="meta-item">
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M9 12l2 2 4-4" />
                                        <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z" />
                                    </svg>
                                    <span>
                                        #{certification.credential_id || certification.certificationId || 'N/A'}
                                    </span>
                                </div>
                            </div>
                            <div className="certification-description">
                                <p>
                                    {certification.summary || certification.description || 'No description available.'}
                                </p>
                            </div>
                        </div>
                    </div>
                    {showActions && (
                        <div className="certification-actions">
                            <button
                                className="certification-edit-btn"
                                onClick={() => handleEditCertification(certification)}
                            >
                                <img src={editIcon} alt="Edit" />
                            </button>
                            <button
                                className="certification-delete-btn"
                                onClick={() => handleDeleteCertification(certification)}
                            >
                                <img src={deleteIcon} alt="Delete" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const calculateProfileCompletion = (apiData) => {
    const personal = apiData?.personalDetails ?? {};
    const social = apiData?.socialData ?? {};
    let completedFields = 0;
    let totalFields = 0;

    // Personal details (Core information - 25% weight)
    if (apiData?.userId?.fullName && apiData?.userId?.fullName.trim() !== '') completedFields++;
    totalFields++;
    if (apiData?.userId?.email && apiData?.userId?.email.trim() !== '') completedFields++;
    totalFields++;
    if (personal?.contact_number && personal?.contact_number.trim() !== '') completedFields++;
    totalFields++;
    if (personal?.current_location && personal?.current_location.trim() !== '') completedFields++;
    totalFields++;

    // Social profiles (Professional networking - 15% weight)
    if (social?.linkedin_url && social?.linkedin_url.trim() !== '') completedFields++;
    totalFields++;
    if (social?.github_username && social?.github_username.trim() !== '') completedFields++;
    totalFields++;

    // Experience (Professional background - 20% weight)
    if (apiData?.experience && Array.isArray(apiData?.experience) && apiData?.experience.length > 0) {
        // Check if experience entries have meaningful data
        const hasValidExperience = apiData?.experience.some(
            (exp) =>
                (exp && exp?.designation && exp?.designation.trim() !== '') ||
                (exp?.company_name && exp?.company_name.trim() !== '') ||
                (exp?.description && exp?.description.trim() !== '')
        );
        if (hasValidExperience) completedFields++;
    }
    totalFields++;

    // Education (Academic background - 15% weight)
    if (apiData?.education && Array.isArray(apiData?.education) && apiData?.education.length > 0) {
        // Check if education entries have meaningful data
        const hasValidEducation = apiData?.education.some(
            (edu) =>
                (edu && edu?.degree && edu?.degree.trim() !== '') ||
                (edu?.institution && edu?.institution.trim() !== '') ||
                (edu?.specialization && edu?.specialization.trim() !== '')
        );
        if (hasValidEducation) completedFields++;
    }
    totalFields++;

    // Skills (Technical competencies - 15% weight)
    if (apiData?.skills && Array.isArray(apiData?.skills) && apiData?.skills?.length > 0) {
        // Check if skills array has valid entries (supporting both string and object formats)
        const validSkills = apiData?.skills?.filter(
            (skill) =>
                (skill && typeof skill === 'string' && skill?.trim() !== '') ||
                (skill && typeof skill === 'object' && skill?.title && skill?.title?.trim() !== '')
        );
        if (validSkills.length > 0) completedFields++;
    }
    totalFields++;

    // Projects (Portfolio - 10% weight)
    if (apiData?.projects && Array.isArray(apiData?.projects) && apiData?.projects?.length > 0) {
        // Check if project entries have meaningful data
        const hasValidProjects = apiData?.projects?.some(
            (project) =>
                (project && project?.name && project?.name.trim() !== '') ||
                (project?.description && project?.description.trim() !== '') ||
                (project?.technologies_used &&
                    Array.isArray(project?.technologies_used) &&
                    project?.technologies_used?.length > 0)
        );
        if (hasValidProjects) completedFields++;
    }
    totalFields++;

    // Certifications (Professional credentials - 10% weight)
    if (apiData?.certifications && Array.isArray(apiData?.certifications) && apiData?.certifications?.length > 0) {
        // Check if certification entries have meaningful data
        const hasValidCertifications = apiData?.certifications?.some(
            (cert) =>
                (cert && cert?.certification_name && cert?.certification_name.trim() !== '') ||
                (cert?.issued_by && cert?.issued_by.trim() !== '') ||
                (cert?.credential_id && cert?.credential_id.trim() !== '')
        );
        if (hasValidCertifications) completedFields++;
    }
    totalFields++;

    // Additional fields for more comprehensive completion
    // Resume/CV (if available)
    if (apiData?.resume_url || apiData?.cv_url) completedFields++;
    totalFields++;

    // Profile picture (if available)
    if (apiData?.profile_picture || apiData?.avatar_url) completedFields++;
    totalFields++;

    return Math.round((completedFields / totalFields) * 100);
};

// Helper function to get completion status for each section
export const getCompletionStatus = (apiData) => {
    const personal = apiData?.personalDetails ?? {};
    const social = apiData?.socialData ?? {};
    const status = {
        personal: {
            completed: 0,
            total: 4,
            fields: ['Full Name', 'Email', 'Phone Number', 'Location'],
        },
        social: {
            completed: 0,
            total: 2,
            fields: ['LinkedIn Profile', 'GitHub Profile'],
        },
        experience: {
            completed: 0,
            total: 1,
            fields: ['Work Experience'],
        },
        education: {
            completed: 0,
            total: 1,
            fields: ['Education'],
        },
        skills: {
            completed: 0,
            total: 1,
            fields: ['Skills'],
        },
        projects: {
            completed: 0,
            total: 1,
            fields: ['Projects'],
        },
        certifications: {
            completed: 0,
            total: 1,
            fields: ['Certifications'],
        },
    };

    // Personal details
    if (apiData?.userId?.fullName && apiData?.userId?.fullName.trim() !== '') status.personal.completed++;
    if (apiData?.userId?.email && apiData?.userId?.email.trim() !== '') status.personal.completed++;
    if (personal?.contact_number && personal.contact_number.trim() !== '') status.personal.completed++;
    if (personal?.current_location && personal.current_location.trim() !== '') status.personal.completed++;

    // Social profiles
    if (social?.linkedin_url && social.linkedin_url.trim() !== '') status.social.completed++;
    if (social?.github_username && social.github_username.trim() !== '') status.social.completed++;

    // Experience
    if (apiData?.experience && Array.isArray(apiData.experience) && apiData.experience.length > 0) {
        const hasValidExperience = apiData.experience.some(
            (exp) =>
                (exp && exp.designation && exp.designation.trim() !== '') ||
                (exp.company_name && exp.company_name.trim() !== '') ||
                (exp.description && exp.description.trim() !== '')
        );
        if (hasValidExperience) status.experience.completed++;
    }

    // Education
    if (apiData?.education && Array.isArray(apiData.education) && apiData.education.length > 0) {
        const hasValidEducation = apiData.education.some(
            (edu) =>
                (edu && edu.degree && edu.degree.trim() !== '') ||
                (edu.institution && edu.institution.trim() !== '') ||
                (edu.specialization && edu.specialization.trim() !== '')
        );
        if (hasValidEducation) status.education.completed++;
    }

    // Skills
    if (apiData?.skills && Array.isArray(apiData.skills) && apiData.skills.length > 0) {
        const validSkills = apiData.skills.filter(
            (skill) =>
                (skill && typeof skill === 'string' && skill.trim() !== '') ||
                (skill && typeof skill === 'object' && skill?.title && skill?.title?.trim() !== '')
        );
        if (validSkills.length > 0) status.skills.completed++;
    }

    // Projects
    if (apiData?.projects && Array.isArray(apiData.projects) && apiData.projects.length > 0) {
        const hasValidProjects = apiData.projects.some(
            (project) =>
                (project && project.name && project.name.trim() !== '') ||
                (project.description && project.description.trim() !== '') ||
                (project.technologies_used &&
                    Array.isArray(project.technologies_used) &&
                    project.technologies_used.length > 0)
        );
        if (hasValidProjects) status.projects.completed++;
    }

    // Certifications
    if (apiData?.certifications && Array.isArray(apiData.certifications) && apiData.certifications.length > 0) {
        const hasValidCertifications = apiData.certifications.some(
            (cert) =>
                (cert && cert.certification_name && cert.certification_name.trim() !== '') ||
                (cert.issued_by && cert.issued_by.trim() !== '') ||
                (cert.credential_id && cert.credential_id.trim() !== '')
        );
        if (hasValidCertifications) status.certifications.completed++;
    }

    return status;
};

// Helper function to get missing fields for profile completion
export const getMissingFields = (apiData) => {
    const personal = apiData?.personalDetails ?? {};
    const social = apiData?.socialData ?? {};
    const missingFields = [];

    // Personal details
    if (!apiData?.userId?.fullName || apiData?.userId?.fullName.trim() === '') missingFields.push('Full Name');
    if (!apiData?.userId?.email || apiData?.userId?.email.trim() === '') missingFields.push('Email');
    if (!personal?.contact_number || personal?.contact_number.trim() === '') missingFields.push('Phone Number');
    if (!personal?.current_location || personal?.current_location.trim() === '') missingFields.push('Location');

    // Social profiles
    if (!social?.linkedin_url || social?.linkedin_url.trim() === '') missingFields.push('LinkedIn Profile');
    if (!social?.github_username || social?.github_username.trim() === '') missingFields.push('GitHub Profile');

    // Experience
    if (!apiData?.experience || !Array.isArray(apiData?.experience) || apiData?.experience?.length === 0) {
        missingFields.push('Work Experience');
    } else {
        const hasValidExperience = apiData?.experience?.some(
            (exp) =>
                (exp && exp?.designation && exp?.designation.trim() !== '') ||
                (exp?.company_name && exp?.company_name.trim() !== '') ||
                (exp?.description && exp?.description.trim() !== '')
        );
        if (!hasValidExperience) missingFields.push('Work Experience Details');
    }

    // Education
    if (!apiData?.education || !Array.isArray(apiData?.education) || apiData?.education?.length === 0) {
        missingFields.push('Education');
    } else {
        const hasValidEducation = apiData?.education?.some(
            (edu) =>
                (edu && edu?.degree && edu?.degree.trim() !== '') ||
                (edu?.institution && edu?.institution.trim() !== '') ||
                (edu?.specialization && edu?.specialization.trim() !== '')
        );
        if (!hasValidEducation) missingFields.push('Education Details');
    }

    // Skills
    if (!apiData?.skills || !Array.isArray(apiData?.skills) || apiData?.skills?.length === 0) {
        missingFields.push('Skills');
    } else {
        const validSkills = apiData?.skills?.filter(
            (skill) =>
                (skill && typeof skill === 'string' && skill?.trim() !== '') ||
                (skill && typeof skill === 'object' && skill?.title && skill?.title?.trim() !== '')
        );
        if (validSkills.length === 0) missingFields.push('Skills');
    }

    // Projects
    if (!apiData?.projects || !Array.isArray(apiData?.projects) || apiData?.projects?.length === 0) {
        missingFields.push('Projects');
    } else {
        const hasValidProjects = apiData?.projects?.some(
            (project) =>
                (project && project?.name && project?.name.trim() !== '') ||
                (project?.description && project?.description.trim() !== '') ||
                (project?.technologies_used &&
                    Array.isArray(project?.technologies_used) &&
                    project.technologies_used.length > 0)
        );
        if (!hasValidProjects) missingFields.push('Project Details');
    }

    // Certifications
    if (!apiData?.certifications || !Array.isArray(apiData?.certifications) || apiData?.certifications?.length === 0) {
        missingFields.push('Certifications');
    } else {
        const hasValidCertifications = apiData?.certifications?.some(
            (cert) =>
                (cert && cert?.certification_name && cert?.certification_name.trim() !== '') ||
                (cert?.issued_by && cert?.issued_by.trim() !== '') ||
                (cert?.credential_id && cert?.credential_id.trim() !== '')
        );
        if (!hasValidCertifications) missingFields.push('Certification Details');
    }

    return missingFields;
};

// Helper function to format date from API response
export const formatDate = (dateObj) => {
    if (!dateObj || !dateObj.year) return '';
    const month = dateObj.month ? dateObj.month.toString().padStart(2, '0') : '01';
    const day = dateObj.day ? dateObj.day.toString().padStart(2, '0') : '01';
    const year = dateObj.year;
    return `${year}-${month}-${day}`;
};

// Helper function to format date for display
export const formatDisplayDate = (dateObj) => {
    if (!dateObj || !dateObj.year) return 'N/A';
    if (dateObj.year === 'null' || dateObj.year === null) return 'N/A';
    if (dateObj.month === 'null' || dateObj.month === null) return 'N/A';
    if (dateObj.day === 'null' || dateObj.day === null) return 'N/A';

    const month = dateObj.month ? dateObj.month : 1;
    const year = dateObj.year;
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[month - 1]} ${year}`;
};

// Helper function to calculate duration
export const calculateDuration = (startDate, endDate) => {
    if (!startDate || !startDate?.year) return 'N/A';
    if (startDate?.year === 'null' || startDate?.year === null) return 'N/A';
    if (startDate?.month === 'null' || startDate?.month === null) return 'N/A';
    if (startDate?.day === 'null' || startDate?.day === null) return 'N/A';

    const start = new Date(startDate?.year, (startDate?.month || 1) - 1, startDate?.day || 1);
    const end =
        endDate && endDate?.year && endDate?.year !== 'null' && endDate?.year !== null
            ? new Date(endDate?.year, (endDate?.month || 1) - 1, endDate?.day || 1)
            : new Date();

    let totalMonths = (end?.getFullYear() - start?.getFullYear()) * 12 + (end?.getMonth() - start?.getMonth());
    if (totalMonths < 0) totalMonths = 0; // safety check

    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;

    if (years > 0 && months > 0) {
        return `${years} year${years > 1 ? 's' : ''} ${months} month${months > 1 ? 's' : ''}`;
    } else if (years > 0) {
        return `${years} year${years > 1 ? 's' : ''}`;
    } else {
        return `${months} month${months !== 1 ? 's' : ''}`;
    }
};
